import _, {difference, isEqual} from 'lodash';

import {ConversationTeamStatusesEnum} from '../../models/api/conversationCommonTypes';
import {ConversationModel} from '../../models/api/conversationModel';
import {
  ConversationOverlayModel,
  ConversationOverlayTypesEnum,
} from '../../models/api/conversationOverlayModel';
import {CellsContext} from '../../store/panels/cells/context/cellsContext';
import {ConversationStatusesEnum, findConversationStatus} from './conversationHelpers';

interface MaybeBuildOverlayOptions {
  newConversation: ConversationModel;
  cellsContext: CellsContext;
  existingConversation?: ConversationModel;
  requesterId?: number;
}

export function maybeBuildConversationOverlay(options: MaybeBuildOverlayOptions) {
  const {newConversation, cellsContext, existingConversation, requesterId} = options;

  if (!existingConversation) {
    return undefined;
  }

  return buildConversationOverlay(existingConversation, newConversation, cellsContext, requesterId);
}

function buildConversationOverlay(
  existingConversation: ConversationModel,
  newConversation: ConversationModel,
  cellsContext: CellsContext,
  requesterId?: number,
): ConversationOverlayModel | undefined {
  // Find the type of action that was performed.
  const conversationOverlayType = findConversationOverlayType(
    existingConversation,
    newConversation,
    cellsContext,
    requesterId,
  );
  if (!conversationOverlayType) {
    return undefined;
  }

  // Add meta-data about the current state of the conversation.
  const {assigneeId, inboxIds} = newConversation;
  const inboxId = inboxIds[0];

  return {
    type: conversationOverlayType,
    conversationId: newConversation.id,
    requesterId,
    assigneeId,
    inboxId,
  };
}

function findConversationOverlayType(
  existingConversation: ConversationModel,
  newConversation: ConversationModel,
  cellsContext: CellsContext,
  requesterId?: number,
) {
  // Assigned.
  if (existingConversation.assigneeId !== newConversation.assigneeId) {
    return ConversationOverlayTypesEnum.ASSIGNED;
  }

  // Moved.
  if (difference(existingConversation.inboxIds, newConversation.inboxIds).length > 0) {
    return ConversationOverlayTypesEnum.MOVED;
  }

  const newStatus = findConversationStatus(cellsContext, newConversation);
  const existingStatus = findConversationStatus(cellsContext, existingConversation);

  // If the status did not change, stop here.
  if (newStatus === existingStatus) {
    return undefined;
  }

  const wasArchived = newStatus === ConversationStatusesEnum.ARCHIVED;
  const wasUnarchived =
    existingStatus === ConversationStatusesEnum.ARCHIVED && newStatus === ConversationStatusesEnum.OPEN;

  const addedReminders = _(newConversation.reminders)
    .filter((r) => r.teammateId === requesterId)
    .differenceWith(existingConversation.reminders, isEqual)
    .value();

  // Snoozed.
  if (wasArchived && addedReminders.length > 0) {
    return ConversationOverlayTypesEnum.SNOOZED;
  }

  // Archived.
  if (wasArchived) {
    return ConversationOverlayTypesEnum.ARCHIVED;
  }

  // Unarchived.
  if (wasUnarchived) {
    return ConversationOverlayTypesEnum.REOPENED;
  }

  // Trashed.
  if (newConversation.teamStatus === ConversationTeamStatusesEnum.TRASHED) {
    return ConversationOverlayTypesEnum.TRASHED;
  }

  // Restored.
  if (existingConversation.teamStatus === ConversationTeamStatusesEnum.TRASHED) {
    return ConversationOverlayTypesEnum.RESTORED;
  }

  return undefined;
}
